import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Accordion, AccordionBody, AccordionHeader, Button, CardBody, Collapse} from "@material-tailwind/react";
import {useUserContext} from "../../types/contexts/UserContext";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {
    PayoutLimit, PayoutLimitForm,
    PayoutLimitsBrokerResponse, PayoutLimitsCurrencyResponse, PayoutLimitsResponse,
} from "../../types/Responses/PayoutLimitsResponse";
import BrokerAvatar from "../../components/BrokerAvatar";
import {API} from "../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../hooks/errorHandler";
import {DataTableRequest} from "../../types/Requests/API/DataTableRequest";
import {useSystemContext} from "../../types/contexts/SystemContext";
import FilterDropdown from "../../components/FilterDropdown";
import DataTable from 'components/DataTable/DataTable';
import TD from 'components/DataTable/TD';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import useComponentVisible from "../../hooks/useComponentVisible";
import Modal from "../../components/Modal/Modal";
import FooterMenu, {FooterListItem} from "../../components/Sidebar/FooterMenu";
import {Input} from "@material-tailwind/react/components/Input";
import {clear, validateData} from "../../reducers/validationReducer";
import {InputType} from "../../types/InputType";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store";
import {Language} from "../../types/Responses/LanguagesResponse";
import {getError, hasError} from "../../hooks/errors";
import ProviderAvatar from "../../components/ProviderAvatar";
import TableSkeleton from "../../components/Skeletons/TableSkeleton";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {RoleLimit, RolesShortResponse} from "../../types/Responses/RolesAndPermissions";
import {APILink} from "../../types/APILink";
import {checkEnabled2FA} from "../../hooks/checkEnabled2FA";
import {setSidebarTitle} from "../../reducers/sidebar";

const PayoutLimits = () => {
    const {t} = useTranslation();

    const user = useUserContext();
    const toast = useToastContext();
    const system = useSystemContext();
    const handleError = useErrorHandler();
    const [roles, setRoles] = useState<RolesShortResponse>();
    const [roleLimits, setRoleLimits] = useState<RoleLimit[]>([]);
    const [brokers, setBrokers] = useState<PayoutLimitsBrokerResponse>();
    const [payoutLimits, setPayoutLimits] = useState<PayoutLimitsBrokerResponse>();
    const [currencies, setCurrencies] = useState<PayoutLimitsCurrencyResponse>();
    const [limits, setLimits] = useState<PayoutLimitsResponse>();
    const [broker, setBroker] = useState<number>(0);
    const [provider, setProvider] = useState<number>(0);
    const [currency, setCurrency] = useState<number>(0);
    const [modalLimit, setModalLimit] = useState<PayoutLimitForm>();
    const [modalCurrencies, setModalCurrencies] = useState<FooterListItem[]>([]);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);
    const {ref, isComponentVisible, setIsComponentVisible} = useComponentVisible(false, '#payment_method');
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);
    const [dataTableRequest, setDataTableRequest] = useState<DataTableRequest>({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    });

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('provider') && typeof request['provider'] !== "undefined" && parseInt(request['provider']) === 0)
            delete request['provider'];
        if (keys.includes('currency') && typeof request['currency'] !== "undefined" && parseInt(request['currency']) === 0)
            delete request['currency'];
        return request;
    }

    const sendRolesRequest = useCallback(() => {
        API.getShortRoles().then((response: AxiosResponse) => {
            setRoles(response.data as RolesShortResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_SHORT_ROLES}`, toast, handleError));
    }, [toast, handleError]);

    const getRoleLimitsRequest = useCallback((id: number) => {
        API.getRoleLimits(id).then((response: AxiosResponse) => {
            setRoleLimits(response.data.data as RoleLimit[]);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_ROLE_LIMITS.replace('$limit', id.toString())}`, toast, handleError));
    }, [toast, handleError]);

    const sendRequest = useCallback((request: object) => {
        API.getPayoutLimits(clearRequest(request)).then((response: AxiosResponse) => {
            setPayoutLimits(response.data as PayoutLimitsBrokerResponse);
            if (!brokers)
                setBrokers(response.data as PayoutLimitsBrokerResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYOUT_LIMITS}`, toast, handleError));
    }, [brokers, setBrokers, setPayoutLimits, toast, handleError]);

    const sendCurrenciesRequest = useCallback(() => {
        API.getPayoutLimitCurrencies().then((response: AxiosResponse) => {
            setCurrencies(response.data as PayoutLimitsCurrencyResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYOUT_LIMIT_CURRENCIES}`, toast, handleError));
    }, [toast, handleError]);

    const sendPayoutsRequest = useCallback((request: DataTableRequest) => {
        const keys = Object.keys(request);
        if (broker && !keys.includes('broker'))
            request = {...request, ['broker' as keyof DataTableRequest]: broker}
        request = clearRequest(request) as DataTableRequest;

        setIsDataTableLoading(true)
        API.getPayoutLimitsData(request).then((response: AxiosResponse) => {
            setLimits(response.data as PayoutLimitsResponse);
            setIsDataTableLoading(false)
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYOUT_LIMITS_DATA}`, toast, handleError));
    }, [broker, setLimits, setIsDataTableLoading, toast, handleError]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        setDataTableRequest(request);
        request = {
            ...request,
            ['currency' as keyof typeof request]: currency,
            ['provider' as keyof typeof request]: provider
        }
        sendPayoutsRequest(request);
    }, [sendPayoutsRequest, setDataTableRequest, currency, provider]);

    const onBrokerOpen = useCallback((id: number) => {
        setBroker(id);
        onDataTableChanged({...dataTableRequest, page: 1, ['broker' as keyof DataTableRequest]: id});
    }, [dataTableRequest, onDataTableChanged, setBroker]);

    const onCurrencyChanged = useCallback((id: string | number) => {
        if (typeof id === 'string')
            id = parseInt(id);
        setCurrency(id);
        const request = {currency: id, provider, broker}

        sendRequest(request);
        sendPayoutsRequest({...dataTableRequest, ...request, page: 1})
    }, [provider, broker, dataTableRequest, sendRequest, sendPayoutsRequest]);

    const onProviderChanged = useCallback((id: string | number) => {
        if (typeof id === 'string')
            id = parseInt(id);
        setProvider(id);
        const request = {provider: id, currency, broker}

        sendRequest(request);
        sendPayoutsRequest({...dataTableRequest, page: 1, ...request})
    }, [currency, broker, dataTableRequest, sendRequest, sendPayoutsRequest]);

    const deletePayoutLimit = useCallback((limit: PayoutLimit) => {
        if (limits) {
            API.deletePayoutLimit(limit.id).then(() => {
                if (limit) {
                    sendPayoutsRequest({
                        ...dataTableRequest,
                        ['currency' as keyof typeof dataTableRequest]: currency,
                        ['provider' as keyof typeof dataTableRequest]: provider
                    });
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.UPDATE_PAYOUT_LIMIT.replace('$limit', limit.toString())}`, toast, handleError));
        }
    }, [limits, toast, handleError, currency, provider, dataTableRequest, sendPayoutsRequest]);

    const currenciesListForm = useCallback((incomeLimit: PayoutLimitForm) => {
        if (!system?.system?.currencies || !currencies) {
            setModalCurrencies([]);
            return;
        }
        const providerCurrencies = currencies.data.find(c => c.provider === incomeLimit.provider)?.currencies;
        const filteredList = system.system.currencies.filter(i => providerCurrencies?.includes(i.id)).map((i) => {
            return {id: i.id, name: i.code};
        }) as FooterListItem[];
        const modifiedModalLimit = {...incomeLimit};
        if (filteredList.length > 0) {
            modifiedModalLimit.currency = filteredList[0]?.id ?? 0;
        }
        setModalCurrencies(filteredList);
    }, [system, currencies]);

    const onBrokerChangedInForm = useCallback((id: string | number) => {
        if (!modalLimit)
            return;
        if (typeof id === "string")
            id = parseInt(id);
        const modifiedModalLimit = {...modalLimit, broker: id};
        setModalLimit(modifiedModalLimit);
    }, [modalLimit, setModalLimit]);

    const onProviderChangedInForm = useCallback((id: string | number) => {
        if (!modalLimit)
            return;
        if (typeof id === "string")
            id = parseInt(id);
        const modifiedModalLimit = {...modalLimit, provider: id, currency: 0};
        setModalLimit(modifiedModalLimit);
        currenciesListForm(modifiedModalLimit);
    }, [modalLimit, setModalLimit, currenciesListForm]);

    const onAmountChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (!modalLimit)
            return;
        if (e?.currentTarget) {
            setModalLimit({...modalLimit, amount: parseFloat(e.currentTarget.value).toString()});
        }
    }, [modalLimit]);

    const onRoleLimitAmountChanged = useCallback((id: number, e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (!modalLimit || !roleLimits)
            return;
        if (e?.currentTarget) {
            const value = parseFloat(e.currentTarget.value);
            if (roleLimits.find(r => r.role_id === id)) {
                const newRoleLimits = [...roleLimits];
                let roleId = 0;
                for (let i = 0; i < roleLimits.length; i++) {
                    // @ts-expect-error can be undefined
                    if (roleLimits[i] && roleLimits[i].role_id === id)
                        roleId = i;
                }
                newRoleLimits[roleId] = {role_id: id, amount: value};
                setRoleLimits(newRoleLimits);
            } else
                setRoleLimits([...roleLimits, {role_id: id, amount: value}]);
        }
    }, [modalLimit, roleLimits]);

    const onCurrencyChangedInForm = useCallback((id: string | number) => {
        if (!modalLimit)
            return;
        if (typeof id === "string")
            id = parseInt(id);
        setModalLimit({...modalLimit, currency: id});
    }, [modalLimit, setModalLimit]);

    const openModal = useCallback((limit: PayoutLimit) => {
        if (!system?.system?.currencies || !system.system.providers || !payoutLimits || !payoutLimits?.data || payoutLimits.data.length == 0) {
            return;
        }

        const modal = {
            id: limit.id,
            amount: limit.amount,
            currency: (system.system.currencies.find(c => c.code === limit.currency)?.id) ?? 0,
            provider: (system.system.providers.find(p => p.name === limit.provider.name)?.id) ?? 0,
            broker: 0,
        } as PayoutLimitForm;
        if (typeof payoutLimits !== "undefined") {
            modal.broker = payoutLimits.data.find(b => b.name === limit.broker.name)?.id ?? 0
        }
        setModalLimit(modal);
        currenciesListForm(modal);
        getRoleLimitsRequest(limit.id);
        setIsComponentVisible(true);
    }, [system, payoutLimits, currenciesListForm, setIsComponentVisible]);

    const openCreateModal = useCallback(() => {
        if (!system?.system?.currencies || !system.system.providers || !payoutLimits || !payoutLimits?.data || payoutLimits.data.length == 0) {
            return;
        }

        setModalLimit({id: 0, amount: '0', broker: 0, currency: 0, provider: 0});
        setIsComponentVisible(true);
    }, [system, payoutLimits, setIsComponentVisible]);

    const closeModal = useCallback(() => {
        setIsComponentVisible(false);
        setModalLimit({id: 0, amount: '0', broker: 0, currency: 0, provider: 0});
        setRoleLimits([]);
        setIsAccordionOpen(false);
    }, [setIsComponentVisible]);

    const handleSuccessResponse = useCallback((response: AxiosResponse, message: JSX.Element | string) => {
        const language = response.data.data as Language
        if (language) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            closeModal();
            const request = {
                ...dataTableRequest,
                ['currency' as keyof typeof dataTableRequest]: currency,
                ['provider' as keyof typeof dataTableRequest]: provider
            };
            sendRequest({provider, currency, broker});
            sendPayoutsRequest(request);
        }
    }, [toast, closeModal, dataTableRequest, sendRequest, sendPayoutsRequest, provider, currency, broker]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);

        if (!modalLimit || !user || !user.user)
            return;
        if (b) {
            let request = {
                broker_id: modalLimit.broker,
                provider_id: modalLimit.provider,
                currency_id: modalLimit.currency,
                amount: parseFloat(modalLimit.amount.replace(' ', ''))
            };
            if (user.user.permissions.includes('setup roles limitations')) {
                request = {...request, ['role_limits' as keyof typeof request]: roleLimits}
            }
            if (modalLimit.id) {
                const tid = modalLimit.id.toString();
                API.updatePayoutLimit(modalLimit.id, request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.payout_limits.updated.title'))).catch(e => ErrorHandler(e, `put${APILink.UPDATE_PAYOUT_LIMIT.replace('$limit', tid)}`, toast, handleError));
            } else {
                API.createPayoutLimit(request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.payout_limits.created.title'))).catch(e => ErrorHandler(e, `post${APILink.GET_PAYOUT_LIMIT_CURRENCIES}`, toast, handleError));
            }

        }
    }, [setValidated, setInProgress, roleLimits, user, modalLimit, toast, handleError, handleSuccessResponse]);

    const submit = useCallback(() => {
        if (!modalLimit)
            return;

        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {key: 'broker', label: t('admin.payout_limits.table.broker'), type: InputType.number},
                    {key: 'provider', label: t('admin.payout_limits.table.provider'), type: InputType.number},
                    {key: 'currency', label: t('admin.payout_limits.table.currency'), type: InputType.number},
                    {key: 'amount', label: t('admin.payout_limits.table.amount'), type: InputType.number},
                ],
                introducedData: modalLimit,
                callback: callback
            }
        }));

    }, [modalLimit, callback, dispatch]);

    const dataTable = useMemo(() => {
        if (!limits || !payoutLimits)
            return <TableSkeleton/>;

        return <DataTable {...dataTableRequest} page={limits.pages.current}
                          total_pages={limits.pages.total}
                          count={limits.count}
                          nodata={limits.data.length === 0}
                          inProgress={isDataTableLoading}
                          options={{searching: false, length: false}}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.payout_limits.table.provider'), sortBy: 'provider_id'},
            {name: t('admin.payout_limits.table.amount'), sortBy: 'amount'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {limits.data.map((lim, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD><>
                            <ProviderAvatar image={`${window.location.origin}/images/psps/${lim.provider.image}`}/>
                            {lim.provider.name}</>
                        </TD>
                        <TD><>{lim.amount} {lim.currency}</>
                        </TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update payout limit') &&
                                    <Button onClick={() => openModal(lim)}
                                            className="px-5 py-2 text-white bg-black mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete payout limit') &&
                                    <Button onClick={() => deletePayoutLimit(lim)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [user, limits, payoutLimits, isDataTableLoading, dataTableRequest, deletePayoutLimit, openModal, onDataTableChanged]);

    const filters = useMemo(() => {
        if (!system || !system.system?.currencies || !system.system?.providers)
            return <></>

        return <div className="flex justify-between items-center">
            <div>
                <FilterDropdown list={system.system.providers} name={t('admin.payout_limits.table.provider')}
                                selected={provider}
                                onChange={onProviderChanged} className="mb-2 lg:mb-0"/>
                <FilterDropdown list={system.system.currencies.map((i) => {
                    return {id: i.id, name: i.code}
                })} name={t('admin.payout_limits.table.currency')} selected={currency} onChange={onCurrencyChanged}
                                className="mb-2 lg:mb-0"/>
            </div>
            {user?.user.permissions.includes('create payout limit') &&
                <Button onClick={() => openCreateModal()}
                        className="bg-black text-white">{t('admin.payout_limits.new.button')}</Button>}
        </div>
    }, [system, provider, currency, onProviderChanged, onCurrencyChanged]);

    const brokersUI = useMemo(() => {
        if (!payoutLimits?.data)
            return <></>;

        return <div className="mt-4">{payoutLimits.data.map(b => <div key={b.name}
                                                                      className={`${b.id === broker ? 'border border-blue-gray-200 dark:border-blue-gray-500' : ''} rounded-md mb-2`}>
            <div
                className={`text-gray-700 dark:text-gray-300 p-6 flex items-center ${b.id === broker ? '' : 'hover:bg-gray-300 dark:hover:text-black hover:text-black rounded-md border border-blue-gray-200 dark:border-blue-gray-500 cursor-pointer'}`}
                onClick={() => onBrokerOpen(b.id)}>
                <BrokerAvatar image={b.image}/>
                <span>{b.name} </span>
                <span className="inline-block ml-4 font-bold text-gray-500 ">[{b.records}]</span>
            </div>
            <Collapse open={b.id === broker}>
                <div className="px-6 py-0">
                    {b.id === broker ? dataTable : ''}
                </div>
            </Collapse>
        </div>)}</div>;
    }, [broker, payoutLimits, dataTable, onBrokerOpen]);

    const modalUI = useMemo(() => {
        if (!modalLimit || !isComponentVisible || !currencies || !system?.system?.providers || !system.system.currencies || !payoutLimits?.data || !brokers?.data)
            return <></>
        return <Modal refer={ref} showModal={isComponentVisible}
                      title={modalLimit.id ? t('admin.payout_limits.edit_title') : t('admin.payout_limits.create_title')}>
            <>
                <FontAwesomeIcon icon={solid('xmark')} size="lg"
                                 className="cursor-pointer absolute top-8 right-4 dark:text-white"
                                 onClick={() => closeModal()}/>
                <div className="max-w-md w-full">
                    <div className="grid grid-cols-3 items-center">
                        <div>
                            <label>{t('admin.payout_limits.table.broker')}</label>
                        </div>
                        <div className="col-span-2">
                            <FooterMenu selected={''}
                                        id='broker-search'
                                        icon={modalLimit.broker ? <><BrokerAvatar size="xs"
                                                                                  image={brokers.data.find(b => b.id === modalLimit.broker)?.image ?? ''}
                                                                                  className="mr-2"/> {brokers.data.find(b => b.id === modalLimit.broker)?.name}</> : <>
                                            <FontAwesomeIcon icon={solid('users')}
                                                             className="mr-2"/> {t('admin.payout_limits.choose')}</>}
                                        onClick={onBrokerChangedInForm}
                                        list={brokers.data.map((b) => {
                                            return {
                                                id: b.id,
                                                name: b.name,
                                                icon: <BrokerAvatar size="xs" image={b.image}/>
                                            }
                                        })}
                                        searchable={true}
                                        className="mt-2 p-2 rounded-[0.5rem]"
                            />
                        </div>
                    </div>
                </div>
                <div className="max-w-md w-full">
                    <div className="grid grid-cols-3 items-center">
                        <div>
                            <label>{t('admin.payout_limits.table.provider')}</label>
                        </div>
                        <div className="col-span-2">
                            <FooterMenu id='payment_method' selected={''}
                                        icon={modalLimit.provider ? <><FontAwesomeIcon icon={solid('money-bill')}
                                                                                       className="mr-2"/> {system.system.providers.find(p => p.id === modalLimit.provider)?.name}</> : <>
                                            <FontAwesomeIcon icon={solid('money-bill')}
                                                             className="mr-2"/> {t('admin.payout_limits.choose')}</>}
                                        onClick={onProviderChangedInForm}
                                        list={system.system.providers}
                                        searchable={true}
                                        className="mt-2 p-2 rounded-[0.5rem]"
                            />
                        </div>
                    </div>
                </div>
                <div className="max-w-md w-full">
                    <div className="grid grid-cols-3 items-center">
                        <div>
                            <label>{t('admin.payout_limits.table.currency')}</label>
                        </div>
                        <div className="col-span-2">
                            <FooterMenu selected={''}
                                        id='currency-search'
                                        icon={modalLimit.currency ? <><FontAwesomeIcon icon={solid('coins')}
                                                                                       className="mr-2"/> {system.system.currencies.find(p => p.id === modalLimit.currency)?.code}</> : <>
                                            <FontAwesomeIcon icon={solid('coins')}
                                                             className="mr-2"/> {t('admin.payout_limits.choose')}</>}
                                        onClick={onCurrencyChangedInForm}
                                        list={modalCurrencies}
                                        searchable={true}
                                        className="mt-2 p-2 rounded-[0.5rem]"
                            />
                        </div>
                    </div>
                </div>
                <div className="max-w-md w-full mt-4">
                    <div className="grid grid-cols-3 items-center">
                        <div>
                            <label>{t('admin.payout_limits.table.amount')}</label>
                        </div>
                        <div className="col-span-2">
                            <Input crossOrigin={''}
                                   type={'number'}
                                   onChange={onAmountChanged}
                                   value={parseFloat(modalLimit.amount.replace(' ', '')).toString()}
                                   className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                   labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}
                                   placeholder=""/>
                            {validated && hasError('amount', errors) &&
                                <label key={'error-amount'}
                                       className="mt-1 text-red-500 text-xs block">{getError('amount', errors)}</label>}
                        </div>
                    </div>
                </div>

                {user && user.user && user.user.permissions.includes('setup roles limitations') &&
                    <Accordion className="my-4 max-w-md w-full" open={isAccordionOpen}>
                        <AccordionHeader className="text-black dark:text-white"
                                         onClick={() => setIsAccordionOpen(!isAccordionOpen)}>{t('admin.payout_limits.roles_limits.title')}</AccordionHeader>
                        <AccordionBody>
                            <div className="dark:text-gray-500">
                                <p>{t('admin.payout_limits.roles_limits.description')}</p>
                                {roles && roles.data.filter(r => r.id > 2).map(r => <div key={`role_${r.id}`}
                                                                                         className="max-w-md w-full mt-4">
                                    <div className="grid grid-cols-3 items-center">
                                        <div>
                                            <label>{r.name}</label>
                                        </div>
                                        <div className="col-span-2">
                                            <Input crossOrigin={''}
                                                   label={system && system.system ? (system.system.currencies.find(p => p.id === modalLimit.currency)?.code ?? '') : ''}
                                                   name={`role_amount_${r.id}`}
                                                   type={'number'}
                                                   onChange={(e) => onRoleLimitAmountChanged(r.id, e)}
                                                   value={roleLimits.find(l => l.role_id === r.id)?.amount.toString() ?? parseFloat(modalLimit.amount.replace(' ', '')).toString()}
                                                   className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                                   labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}
                                                   placeholder=""/>
                                            {validated && hasError(`role_amount_${r.id}`, errors) &&
                                                <label key={`error-role_amount_${r.id}`}
                                                       className="mt-1 text-red-500 text-xs block">{getError(`role_amount_${r.id}`, errors)}</label>}
                                        </div>
                                    </div>
                                </div>)}
                            </div>
                        </AccordionBody>
                    </Accordion>
                }

                <Button disabled={isInProgress} className="mt-8" color="teal"
                        onClick={() => submit()}>{modalLimit.id ? t('Save') : t('Create')}</Button>
                <span className="mt-1 cursor-pointer text-gray-400 dark:text-gray-700"
                      onClick={() => closeModal()}>{t('close')}</span>
            </>
        </Modal>
    }, [modalLimit, user, roleLimits, isAccordionOpen, modalCurrencies, isInProgress, isComponentVisible, onRoleLimitAmountChanged, currencies, system, brokers, onCurrencyChangedInForm, onProviderChangedInForm, submit, closeModal, errors, onAmountChanged, onBrokerChangedInForm, payoutLimits?.data, ref, validated]);

    useEffect(() => {
        setTitle(t('admin.payout_limits.title'));
        dispatch(setSidebarTitle(t('admin.payout_limits.title')))
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRequest({});
                sendCurrenciesRequest();
                if (user && user.user && user.user.permissions.includes('setup roles limitations')) {
                    sendRolesRequest();
                }
            }).catch()
        }
        dispatch(clear())
    }, [user, dispatch]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {modalUI}
                    {filters}
                    {brokersUI}
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(PayoutLimits);
